import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { LogIn, LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  useEffect(() => {
    // Check authentication status
    const adminAuth = localStorage.getItem('adminAuth');
    const userAuth = localStorage.getItem('userAuth');
    
    setIsAdminLoggedIn(adminAuth ? JSON.parse(adminAuth).authenticated : false);
    setIsUserLoggedIn(userAuth ? JSON.parse(userAuth).authenticated : false);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleAdminLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAdminLoggedIn(false);
    toast.success('Admin logged out successfully');
    navigate('/');
    onClose();
  };
  
  const handleUserLogout = () => {
    localStorage.removeItem('userAuth');
    setIsUserLoggedIn(false);
    toast.success('Logged out successfully');
    navigate('/');
    onClose();
  };

  const handleExternalLogin = () => {
    // This would be replaced with your actual external login URL
    const externalLoginUrl = "https://auth.myqoop.com";
    
    // For demo purposes, we're redirecting to the login page that will then simulate a redirect
    navigate('/login');
    onClose();
    
    // In a real application, we would redirect to the external URL:
    // window.location.href = externalLoginUrl;
    
    toast.info('Redirecting to login portal...');
  };

  if (!isOpen) return null;

  return (
    <div className="md:hidden bg-[#1d2025]/95 backdrop-blur-sm absolute top-full left-0 right-0 border-b border-gray-700 animate-fade-in">
      <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
        <Link 
          to="/" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          Home
        </Link>
        <Link 
          to="/features" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/features') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          Features
        </Link>
        <Link 
          to="/pricing" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/pricing') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          Pricing
        </Link>
        <Link 
          to="/blog" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/blog') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          Blog
        </Link>
        <Link 
          to="/about" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/about') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          About
        </Link>
        <Link 
          to="/faq" 
          className={`text-base font-medium p-2 rounded-md transition-colors ${
            isActive('/faq') ? 'text-[#0EA5E9] bg-[#0EA5E9]/5' : 'text-tranquil-neutral hover:bg-gray-800 hover:text-[#0EA5E9]'
          }`}
          onClick={onClose}
        >
          FAQ
        </Link>

        {/* Auth buttons section */}
        <div className="pt-2 flex flex-col space-y-3">
          {isAdminLoggedIn ? (
            <>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  navigate('/admin-dashboard');
                  onClose();
                }}
              >
                <ShieldCheck className="h-4 w-4 mr-1" />
                Admin Panel
              </Button>
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={handleAdminLogout}
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout (Admin)
              </Button>
            </>
          ) : isUserLoggedIn ? (
            <>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  navigate('/dashboard');
                  onClose();
                }}
              >
                Dashboard
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleUserLogout}
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                className="w-full border-[#33C3F0] text-[#33C3F0] hover:bg-[#33C3F0]/10"
                onClick={handleExternalLogin}
              >
                <LogIn className="h-4 w-4 mr-1" />
                Log in
              </Button>
              <Button 
                className="w-full bg-[#33C3F0] hover:bg-[#0FA0CE] text-white"
                onClick={handleExternalLogin} // Use the same handler as login
              >
                Sign up
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
